import React, { useState } from 'react';
import './ProductList.css';
import './ProductModal.css';

function ProductList({ products, onAddToCart, cart, onUpdateQuantity }) {
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [sortBy, setSortBy] = useState('default'); // 'default', 'name-asc', 'name-desc', 'price-asc', 'price-desc'

  if (products.length === 0) {
    return (
      <div className="product-list-container">
        <div className="empty-catalog">
          <p>Каталог пуст</p>
        </div>
      </div>
    );
  }

  // Функция сортировки товаров
  const getSortedProducts = () => {
    const sorted = [...products];
    
    switch (sortBy) {
      case 'name-asc':
        return sorted.sort((a, b) => a.name.localeCompare(b.name, 'ru'));
      case 'name-desc':
        return sorted.sort((a, b) => b.name.localeCompare(a.name, 'ru'));
      case 'price-asc':
        return sorted.sort((a, b) => a.price - b.price);
      case 'price-desc':
        return sorted.sort((a, b) => b.price - a.price);
      default:
        return sorted;
    }
  };

  const sortedProducts = getSortedProducts();

  const getCartQuantity = (productId) => {
    const cartItem = cart.find(item => item.id === productId);
    return cartItem ? cartItem.quantity : 0;
  };

  const handleProductClick = (product, e) => {
    // Не открываем попап, если клик был по кнопке или контролам количества
    if (e.target.closest('.add-to-cart-button, .quantity-controls, .quantity-button, .quantity-display')) {
      return;
    }
    setSelectedProduct(product);
  };

  const closeModal = () => {
    setSelectedProduct(null);
  };

  const handleModalAddToCart = (product) => {
    onAddToCart(product);
    closeModal();
  };

  const handleModalUpdateQuantity = (productId, quantity) => {
    onUpdateQuantity(productId, quantity);
  };

  return (
    <>
      <div className="product-list-container">
        <div className="sort-controls" style={{
          marginBottom: '1.5rem',
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
          flexWrap: 'wrap'
        }}>
          <label style={{
            fontSize: '0.9375rem',
            fontWeight: '600',
            color: '#212121'
          }}>
            Сортировка:
          </label>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            style={{
              padding: '0.625rem 1rem',
              border: '2px solid #e0e0e0',
              borderRadius: '8px',
              fontSize: '0.9375rem',
              fontFamily: 'inherit',
              backgroundColor: '#ffffff',
              color: '#212121',
              cursor: 'pointer',
              transition: 'border-color 0.2s ease',
              outline: 'none'
            }}
            onFocus={(e) => e.target.style.borderColor = '#1976d2'}
            onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
          >
            <option value="default">По умолчанию</option>
            <option value="name-asc">По названию (А-Я)</option>
            <option value="name-desc">По названию (Я-А)</option>
            <option value="price-asc">По цене (по возрастанию)</option>
            <option value="price-desc">По цене (по убыванию)</option>
          </select>
        </div>
        <div className="product-grid">
          {sortedProducts.map(product => {
            const quantity = getCartQuantity(product.id);
            const isInCart = quantity > 0;

            return (
              <div 
                key={product.id} 
                className="product-card"
                onClick={(e) => handleProductClick(product, e)}
              >
                {product.image_url && (
                  <div className="product-image">
                    <img 
                      src={product.image_url} 
                      alt={product.name}
                      onError={(e) => {
                        console.error('Ошибка загрузки изображения:', product.image_url);
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                )}
                <div className="product-info">
                  <h3 className="product-name">{product.name}</h3>
                  <div className="product-footer">
                    <span className="product-price">{product.price} ₽</span>
                    {isInCart ? (
                      <div className="quantity-controls">
                        <button
                          className="quantity-button minus"
                          onClick={(e) => {
                            e.stopPropagation();
                            onUpdateQuantity(product.id, quantity - 1);
                          }}
                          aria-label="Уменьшить количество"
                        >
                          −
                        </button>
                        <input
                          type="number"
                          className="quantity-display"
                          value={quantity}
                          min="1"
                          onChange={(e) => {
                            e.stopPropagation();
                            const newQuantity = parseInt(e.target.value) || 1;
                            if (newQuantity >= 1) {
                              onUpdateQuantity(product.id, newQuantity);
                            }
                          }}
                          onBlur={(e) => {
                            const newQuantity = parseInt(e.target.value) || 1;
                            if (newQuantity < 1) {
                              onUpdateQuantity(product.id, 1);
                            }
                          }}
                          onClick={(e) => e.stopPropagation()}
                          aria-label="Количество товара"
                        />
                        <button
                          className="quantity-button plus"
                          onClick={(e) => {
                            e.stopPropagation();
                            onUpdateQuantity(product.id, quantity + 1);
                          }}
                          aria-label="Увеличить количество"
                        >
                          +
                        </button>
                      </div>
                    ) : (
                      <button
                        className="add-to-cart-button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onAddToCart(product);
                        }}
                      >
                        В корзину
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {selectedProduct && (
        <ProductModal
          product={selectedProduct}
          onClose={closeModal}
          onAddToCart={handleModalAddToCart}
          cart={cart}
          onUpdateQuantity={handleModalUpdateQuantity}
        />
      )}
    </>
  );
}

function ProductModal({ product, onClose, onAddToCart, cart, onUpdateQuantity }) {
  const quantity = cart.find(item => item.id === product.id)?.quantity || 0;
  const isInCart = quantity > 0;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="product-modal-backdrop" onClick={handleBackdropClick}>
      <div className="product-modal" onClick={(e) => e.stopPropagation()}>
        <button className="product-modal-close" onClick={onClose} aria-label="Закрыть">
          ×
        </button>
        
        {product.image_url && (
          <div className="product-modal-image">
            <img 
              src={product.image_url} 
              alt={product.name}
              onError={(e) => {
                console.error('Ошибка загрузки изображения:', product.image_url);
                e.target.style.display = 'none';
              }}
            />
          </div>
        )}

        <div className="product-modal-content">
          <h2 className="product-modal-name">{product.name}</h2>
          
          {product.description && (
            <div className="product-modal-description">
              <p>{product.description}</p>
            </div>
          )}

          <div className="product-modal-footer">
            <span className="product-modal-price">{product.price} ₽</span>
            {isInCart ? (
              <div className="product-modal-quantity-controls">
                <button
                  className="quantity-button minus"
                  onClick={() => onUpdateQuantity(product.id, quantity - 1)}
                  aria-label="Уменьшить количество"
                >
                  −
                </button>
                <input
                  type="number"
                  className="quantity-display"
                  value={quantity}
                  min="1"
                  onChange={(e) => {
                    const newQuantity = parseInt(e.target.value) || 1;
                    if (newQuantity >= 1) {
                      onUpdateQuantity(product.id, newQuantity);
                    }
                  }}
                  onBlur={(e) => {
                    const newQuantity = parseInt(e.target.value) || 1;
                    if (newQuantity < 1) {
                      onUpdateQuantity(product.id, 1);
                    }
                  }}
                  aria-label="Количество товара"
                />
                <button
                  className="quantity-button plus"
                  onClick={() => onUpdateQuantity(product.id, quantity + 1)}
                  aria-label="Увеличить количество"
                >
                  +
                </button>
              </div>
            ) : (
              <button
                className="add-to-cart-button"
                onClick={() => onAddToCart(product)}
              >
                В корзину
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProductList;

